/**
 * AI CONTEXT
 * Purpose: makeup-session CRUD/search table.
 * Key deps: useMakeupTableData, AddMakeupFormModal, makeup table columns.
 * Referenced by: App route ROUTES.MAKEUPS.
 * Importance: attendance continuity after missed sessions.
 * Refactor: align loading/error state handling with other table pages.
 */
import { useMemo, useState } from "react"

import EmptyPage from "#blocks/EmptyPage"
import Flex from "#blocks/Flex"
import Page from "#blocks/Page"
import { buildTableColumns } from "#blocks/Table/buildTableColumns"
import Table2 from "#blocks/Table/Table2"
import TableFiltersRow from "#blocks/Table/TableFiltersRow"
import {
  getMakeupSortValue,
  MAKEUP_SORTABLE_FIELDS,
  MAKEUP_TABLE_COLUMNS,
} from "#constants/makeupOptions"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { type MakeupForTable } from "#customTypes/composedTypes/makeupTypes"
import { SETTINGS_KEYS, type Uuid } from "#customTypes/globalTypes"
import AddMakeupFormModal from "#forms/AddMakeup/AddMakeupFormModal"
import useMakeupTableData from "#hooks/dataHooks/useMakeupTableData"
import useHasPermission from "#hooks/newHooks/useHasPermission"

const MakeupsTable = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedMakeupId, setSelectedMakeupId] = useState<Uuid | null>(null)

  const canSeeMakeups = useHasPermission(RESOURCES.MAKEUP, USER_ACTIONS.READ)
  const canViewId = useHasPermission(RESOURCES.ID, USER_ACTIONS.READ)

  const {
    makeupsForTable,
    isReady,
    onSearch,
    sortField,
    sortOrder,
    onSortChange,
  } = useMakeupTableData()

  const columns = useMemo(
    () =>
      buildTableColumns<MakeupForTable>(MAKEUP_TABLE_COLUMNS, {
        sortableFields: MAKEUP_SORTABLE_FIELDS,
        getSortValue: getMakeupSortValue,
        showId: canViewId,
      }),
    [canViewId]
  )

  const handleOpen = () => {
    if (!canSeeMakeups) {
      return
    }

    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
    setSelectedMakeupId(null)
  }

  const handleRowClick = (selectedMakeupId: string) => {
    setSelectedMakeupId(selectedMakeupId)
    handleOpen()
  }

  if (!canSeeMakeups) {
    return (
      <Page>
        <EmptyPage />
      </Page>
    )
  }

  return (
    <Page>
      <Flex col style={{ padding: 20, width: "100%", maxWidth: 2200 }} gap={12}>
        <h1>Makeups</h1>

        <TableFiltersRow
          onAdd={handleOpen}
          onSearch={onSearch}
          searchPlaceholder="Search by name, missed class, makeup class"
          settingsKey={SETTINGS_KEYS.MAKEUPS}
        />

        {isOpen && (
          <AddMakeupFormModal
            onClose={handleClose}
            makeupId={selectedMakeupId || undefined}
          />
        )}
        <Table2<MakeupForTable>
          isLoading={!isReady}
          data={makeupsForTable}
          columns={columns}
          onRowClick={handleRowClick}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={onSortChange}
        />
      </Flex>
    </Page>
  )
}

export default MakeupsTable
